import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { InviteSearchResult } from '../../../../core/models/invite-search-result';
import { PagedList } from '../../../../core/models/pagination';
import { InviteService } from '../../../../core/services/api/invite.service';
import { Options } from '../../../../shared/components/pagination/pagination.component';
import { BasicModalOperationResult, DialogModalComponent, IBasicModalComponentInstance } from '../../../../shared/components/dialog-modal/dialog-modal.component';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit {
  searchTerm: string = '';
  pagedResults: PagedList<InviteSearchResult> | null = null;
  showSpinner: boolean = false;
  loadError: boolean = false;
  options: Options = { page: 1, size: 10 };

  constructor(
    private inviteService: InviteService,
    private modalService: NgbModal,
    public translate: TranslateService
  ) { }

  async ngOnInit(): Promise<void> {
    await this.loadData(this.options.page, this.options.size);
  }

  loadData = async (page?: number, size?: number): Promise<void> => {
    this.showSpinner = true;
    this.loadError = false;
    try {
      this.pagedResults = await this.inviteService.searchUsers(this.searchTerm.trim(), page, size);
      this.options.page = page;
    } catch (error) {
      console.error('Error al cargar invitaciones:', error);
      this.loadError = true;
      this.pagedResults = null;
    } finally {
      this.showSpinner = false;
    }
  }

  async onSearch(): Promise<void> {
    await this.loadData(1, this.options.size);
  }

  canResend(result: InviteSearchResult): boolean {
    return result.credentialStatus === 'pendiente';
  }

  resendTooltip(result: InviteSearchResult): string {
    if (result.credentialStatus === 'emitida') return 'El usuario ya tiene credencial emitida.';
    if (result.credentialStatus === 'revocada') return 'El usuario tiene la credencial revocada.';
    return 'Reenviar invitación';
  }

  async confirmResend(result: InviteSearchResult) {
    const modalRef = this.modalService.open(DialogModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true
    });
    const instance = modalRef.componentInstance as IBasicModalComponentInstance;
    instance.title = 'Reenviar invitación';
    instance.body = `¿Confirmás el reenvío de la invitación a ${result.fullName}? El QR anterior quedará invalidado.`;
    instance.type = BasicModalOperationResult.Warning;
    instance.buttons = [
      {
        text: 'Cancelar',
        cssClass: 'btn btn-danger',
        closeModalOnClick: true
      },
      {
        text: 'Confirmar',
        cssClass: 'btn btn-success',
        closeModalOnClick: true,
        handler: async () => {
          await this.doResend(result);
        }
      }
    ];
  }

  private async doResend(result: InviteSearchResult) {
    this.showSpinner = true;
    try {
      await this.inviteService.resendInvitation(result.inviteId);
      await this.loadData(this.options.page, this.options.size);
      await this.showSuccess(`La invitación fue reenviada a ${result.fullName}.`);
    } catch {
      await this.showError('Ocurrió un error al reenviar la invitación. Por favor intentá de nuevo.');
    } finally {
      this.showSpinner = false;
    }
  }

  private async showSuccess(message: string) {
    const modalRef = this.modalService.open(DialogModalComponent, { backdrop: 'static', keyboard: false });
    const instance = modalRef.componentInstance as IBasicModalComponentInstance;
    instance.title = 'Reenvío exitoso';
    instance.body = message;
    instance.type = BasicModalOperationResult.Success;
    await modalRef.result;
  }

  private async showError(message: string) {
    const modalRef = this.modalService.open(DialogModalComponent, { backdrop: 'static', keyboard: false });
    const instance = modalRef.componentInstance as IBasicModalComponentInstance;
    instance.title = 'Error';
    instance.body = message;
    instance.type = BasicModalOperationResult.Error;
    await modalRef.result;
  }
}
